import React from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/react/24/outline';

const faqs = [
  {
    question: "What is this platform?",
    answer: "Our platform provides comprehensive analytics and tools for sports betting. Access deep game analysis with multiple time splits, compare team matchup statistics with advanced normalization methods, track real-time betting lines across multiple sportsbooks, view injury reports and lineups, and automate your strategies with custom betting systems. Everything you need to make informed betting decisions in one place."
  },
  {
    question: "What analytics tools are available?",
    answer: "We offer Game Line Analysis to view team performance across different splits (L5, L10, L15, L20, L30) and analyze moneyline, totals, and spread trends by period. Our Matchup Stats feature lets you compare teams using raw averages, z-scores, league ranks, percentiles, or min-max scaling with radar chart visualizations. Player Stats show season overviews and last 10 game logs to track trends over time."
  },
  {
    question: "What sportsbooks do you track for line movement?",
    answer: "We track betting lines from major sportsbooks including DraftKings, FanDuel, Caesars, BetMGM, and Fanatics. You can view spread, moneyline, and totals across different game periods, see real-time line movements, and identify where to find the best value for your bets."
  },
  {
    question: "What sports and leagues are available?",
    answer: "Currently our platform covers MLB, NFL, and NBA with comprehensive data going back to 2018. This includes game statistics, betting lines, injury reports, lineups, and weather conditions (for outdoor sports). We are working on expanding our offerings to include more sports and leagues in the future!"
  },
  {
    question: "How do I use the game line analysis?",
    answer: "The game line analysis tool lets you select different time splits (like last 5, 10, 15, 20, or 30 games) to see team performance trends. You can analyze by game period to see moneyline trends, view combined team totals by period, and compare scores against the spread. This helps you identify patterns and make more informed betting decisions based on recent performance."
  },
  {
    question: "Can I create my own betting systems?",
    answer: "Yes! Use the insights from our analytics to build automated betting systems. Navigate to the \"Systems\" page to define custom criteria based on team statistics, odds thresholds, and more. Backtest your systems against historical data to see how they would have performed, then get notified when upcoming games match your criteria. Systems can be kept private or shared with the community."
  },
  {
    question: "Is there a limit to how many systems I can create or follow?",
    answer: "No limits. We believe in giving you the freedom to create and follow as many systems as you want. Experiment with different strategies and find what works best for you."
  },
  {
    question: "Where can I get help if I have more questions or feature ideas?",
    answer: "If you have further questions or want to recommend changes to the platform, please join our Discord or direct message us on Twitter. We're here to help you make the most of our platform and ensure your betting strategies are as successful as possible."
  }
];

const FAQ = () => {
  return (
    <div className="bg-white dark:bg-brand-dark-900">
      <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
            Frequently asked questions
          </h2>
          <dl className="mt-10 space-y-6 divide-y divide-gray-200 dark:divide-white/10">
            {faqs.map((faq) => (
              <Disclosure as="div" key={faq.question} className="pt-6 first:pt-0">
                <dt>
                  <DisclosureButton className="group flex w-full items-start justify-between text-left text-gray-900 dark:text-white">
                    <span className="text-lg font-semibold">{faq.question}</span>
                    <span className="ml-6 flex h-7 items-center">
                      <PlusSmallIcon aria-hidden="true" className="size-6 text-brand-red-500 dark:text-brand-red-400 group-data-open:hidden" />
                      <MinusSmallIcon aria-hidden="true" className="size-6 text-brand-red-500 dark:text-brand-red-400 group-not-data-open:hidden" />
                    </span>
                  </DisclosureButton>
                </dt>
                <DisclosurePanel as="dd" className="mt-2 pr-12 pb-6">
                  <p className="text-base/7 text-gray-600 dark:text-gray-300">{faq.answer}</p>
                </DisclosurePanel>
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default FAQ;